import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Trash2, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const CONV_KEY = "dcc_agent_conv";
const SESSION_KEY = "dcc_cart_session"; // shared with cart context

function getSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch { return null; }
}

// Routes where the widget should NOT appear
const HIDDEN_ROUTE_PATTERNS = [
  /^\/checkout(\/|$)/,
  /^\/app\/staff(\/|$)/,
  /^\/app\/superadmin(\/|$)/,
  /^\/app\/partner(\/|$)/,
  /^\/setup-2fa/,
];

function shouldHideWidget(pathname) {
  return HIDDEN_ROUTE_PATTERNS.some((r) => r.test(pathname));
}

const SUGGESTIONS = [
  "Muéstrame diplomados en riesgo",
  "¿Cómo funciona un programa a la medida?",
  "Quiero hablar con un asesor",
  "¿Tienen certificaciones bursátiles?",
];

const WELCOME = (
  "¡Hola! 👋 Soy el Asesor DCC, tu asistente IA. Puedo ayudarte a:\n\n" +
  "• Encontrar programas del catálogo (banca, riesgo, tesorería, mercados bursátiles...)\n" +
  "• Explicarte precios y próximas cohortes\n" +
  "• Diseñar un programa a la medida con IA\n" +
  "• Conectarte con un asesor humano\n\n" +
  "¿En qué te puedo ayudar hoy?"
);

export default function AgentChatWidget() {
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => {
    try { return localStorage.getItem(CONV_KEY); } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null); // {tool_call_id, tool_name, args, human_preview}
  const [navSuggestion, setNavSuggestion] = useState(null);
  const [hasPulse, setHasPulse] = useState(true);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const hidden = shouldHideWidget(location.pathname);

  // On mount, load history if we have a conversation_id
  useEffect(() => {
    if (!conversationId) {
      setMessages([{ role: "assistant", content: WELCOME, id: "welcome-local" }]);
      return;
    }
    api.get(`/agent/conversations/${conversationId}/messages`, {
      headers: { "X-Session-Id": getSessionId() },
    })
      .then((r) => {
        const msgs = (r.data.messages || []).filter(
          (m) => m.role === "user" || m.role === "assistant"
        );
        setMessages(msgs.length ? msgs : [{ role: "assistant", content: WELCOME, id: "welcome-local" }]);
      })
      .catch(() => {
        // Conversation lost server-side → reset
        try { localStorage.removeItem(CONV_KEY); } catch {}
        setConversationId(null);
        setMessages([{ role: "assistant", content: WELCOME, id: "welcome-local" }]);
      });
  }, [conversationId]);

  // Autoscroll
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, pending]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const persistConv = useCallback((newId) => {
    if (!newId) return;
    setConversationId(newId);
    try { localStorage.setItem(CONV_KEY, newId); } catch {}
  }, []);

  const handleServerResponse = useCallback((data) => {
    persistConv(data.conversation_id);
    setMessages((m) => [...m, {
      role: "assistant", content: data.reply, id: `a-${Date.now()}`,
      tools: data.tool_calls_executed || [],
    }]);
    if (data.pending_confirmation) {
      setPending(data.pending_confirmation);
      track("agent_tool_called", { tool_name: data.pending_confirmation.tool_name, requires_confirmation: true });
    } else {
      setPending(null);
    }
    if (data.navigate_suggestion) setNavSuggestion(data.navigate_suggestion);
    if (data.cart_updated) {
      try { window.dispatchEvent(new CustomEvent("cart:updated")); } catch {}
    }
    // Track read-only tools
    (data.tool_calls_executed || []).forEach((t) => {
      track("agent_tool_called", { tool_name: t.tool_name, requires_confirmation: false });
    });
  }, [persistConv]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setHasPulse(false);
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    setInput("");
    setPending(null);
    setNavSuggestion(null);
    setSending(true);
    track("agent_message_sent", { length: msg.length, conversation_id: conversationId || "new" });
    try {
      const { data } = await api.post("/agent/chat", {
        conversation_id: conversationId, message: msg, page_path: location.pathname,
      }, { headers: { "X-Session-Id": getSessionId() } });
      handleServerResponse(data);
    } catch (e) {
      const detail = e.response?.data?.detail || "Ocurrió un error. Intenta de nuevo.";
      setMessages((m) => [...m, {
        role: "assistant", content: typeof detail === "string" ? detail : "Ocurrió un error.",
        id: `err-${Date.now()}`, error: true,
      }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, conversationId, location.pathname, handleServerResponse]);

  const confirmPending = useCallback(async (confirm) => {
    if (!pending || !conversationId) return;
    const tid = pending.tool_call_id;
    setPending(null);
    setSending(true);
    track(confirm ? "agent_tool_confirmed" : "agent_tool_cancelled", { tool_name: pending.tool_name });
    try {
      const { data } = await api.post("/agent/tool-confirm", {
        conversation_id: conversationId, tool_call_id: tid, confirm,
      }, { headers: { "X-Session-Id": getSessionId() } });
      handleServerResponse(data);
      if (data.tool_calls_executed?.some((t) => t.tool_name === "escalate_to_human")) {
        track("agent_escalated_to_human", { conversation_id: conversationId });
      }
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant", content: "No pude completar la acción. Intenta de nuevo.",
        id: `err-${Date.now()}`, error: true,
      }]);
    } finally {
      setSending(false);
    }
  }, [pending, conversationId, handleServerResponse]);

  const reset = useCallback(async () => {
    if (conversationId) {
      try {
        await api.post(`/agent/conversations/${conversationId}/close`, {},
          { headers: { "X-Session-Id": getSessionId() } });
        track("agent_conversation_closed", {
          messages_count: messages.filter((m) => m.role === "user").length,
        });
      } catch {}
    }
    try { localStorage.removeItem(CONV_KEY); } catch {}
    setConversationId(null);
    setPending(null);
    setNavSuggestion(null);
    setMessages([{ role: "assistant", content: WELCOME, id: "welcome-local" }]);
  }, [conversationId, messages]);

  const openWidget = () => {
    setOpen(true);
    setHasPulse(false);
    track("agent_widget_opened", { page: location.pathname });
  };

  const doNavigate = () => {
    if (!navSuggestion?.path) return;
    setOpen(false);
    nav(navSuggestion.path);
    setNavSuggestion(null);
  };

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          onClick={openWidget}
          data-testid="agent-chat-toggle"
          aria-label="Abrir asistente DCC"
          className="fixed z-40 bottom-5 right-5 w-14 h-14 rounded-full bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircle className="w-6 h-6" />
          {hasPulse && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#F26A21] animate-ping" />
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="agent-chat-panel"
          className="fixed z-50 bottom-0 right-0 sm:bottom-5 sm:right-5 w-full sm:w-[400px] h-[100dvh] sm:h-[600px] sm:max-h-[80vh] bg-white sm:rounded-xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3 bg-[#0B0B0B] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#F26A21] flex items-center justify-center font-bold text-[#0B0B0B] text-sm">
                D
              </div>
              <div>
                <p className="font-bold text-sm" data-testid="agent-title">Asesor DCC</p>
                <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Asistente IA · en línea
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reset} data-testid="agent-reset-btn"
                aria-label="Reiniciar conversación"
                className="p-1.5 rounded hover:bg-white/10 text-gray-300"
                title="Reiniciar"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)} data-testid="agent-close-btn"
                aria-label="Cerrar chat"
                className="p-1.5 rounded hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-4 bg-[#F9FAFB]"
               data-testid="agent-messages-list">
            <div className="space-y-3">
              {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-gray-500 pl-11" data-testid="agent-typing">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Pensando…
                </div>
              )}
            </div>

            {/* Suggestions (initial state only) */}
            {messages.length <= 1 && !sending && (
              <div className="mt-6 space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold px-1">Sugerencias</p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`agent-suggestion-${i}`}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-[#F26A21] text-[#0B0B0B] transition-colors"
                  >
                    <Sparkles className="inline w-3 h-3 mr-1 text-[#F26A21]" /> {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pending confirmation banner */}
          {pending && (
            <div className="flex-shrink-0 px-3 py-3 bg-amber-50 border-t border-amber-200"
                 data-testid="agent-confirm-panel">
              <p className="text-xs font-semibold text-amber-900 mb-1">Confirmar acción</p>
              <p className="text-xs text-amber-800 mb-2">{pending.human_preview}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => confirmPending(true)}
                  data-testid="agent-confirm-yes"
                  className="flex-1 text-xs font-semibold px-3 py-2 rounded bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"
                >
                  Sí, continuar
                </button>
                <button
                  onClick={() => confirmPending(false)}
                  data-testid="agent-confirm-no"
                  className="flex-1 text-xs font-semibold px-3 py-2 rounded border border-gray-300 text-[#0B0B0B] bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Navigate suggestion banner */}
          {navSuggestion && !pending && (
            <div className="flex-shrink-0 px-3 py-3 bg-[#F26A21]/10 border-t border-[#F26A21]/30"
                 data-testid="agent-nav-panel">
              <p className="text-xs text-[#0B0B0B] mb-2">{navSuggestion.reason || "Ir a la siguiente sección"}</p>
              <button
                onClick={doNavigate}
                data-testid="agent-nav-btn"
                className="w-full inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-2 rounded bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"
              >
                Ir a {navSuggestion.path} <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex-shrink-0 border-t border-gray-200 bg-white p-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault(); send();
                  }
                }}
                placeholder="Escribe tu mensaje..."
                rows={1}
                maxLength={4000}
                disabled={sending || !!pending}
                data-testid="agent-input"
                className="flex-1 resize-none max-h-32 text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F26A21] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={sending || !input.trim() || !!pending}
                data-testid="agent-send-btn"
                aria-label="Enviar mensaje"
                className="p-2 rounded-lg bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-1 text-[9px] text-gray-400 text-center">Powered by DCC AI · Respuestas generadas por IA</p>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}
         data-testid={`agent-msg-${m.role}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F26A21] flex items-center justify-center font-bold text-[#0B0B0B] text-xs">
          D
        </div>
      )}
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#0B0B0B] text-white rounded-tr-sm"
            : m.error
              ? "bg-red-50 border border-red-200 text-red-800 rounded-tl-sm"
              : "bg-white border border-gray-200 text-[#0B0B0B] rounded-tl-sm"
        }`}
      >
        {m.content}
        {/* Tool result chips (subtle) */}
        {!isUser && Array.isArray(m.tools) && m.tools.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {m.tools.map((t, i) => (
              <span key={i}
                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#F26A21]/10 text-[#0B0B0B] border border-[#F26A21]/30 font-mono">
                <Sparkles className="w-2.5 h-2.5" /> {t.tool_name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
